import { useState } from 'react'
import { Copy, Check, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@/hooks/useWallet'
import { useAddressQR } from '@/hooks/useQRCode'
import Skeleton from '@/components/common/Skeleton'
import receiveService from '@/services/wallet/receive.service'

const Receive = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { address } = useWallet()
  const { state: qrState } = useAddressQR(address, {
    size: 300,
    margin: 2,
    autoGenerate: true
  })
  const qrDataUrl = qrState.dataURL
  const qrLoading = qrState.isLoading
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!address) return
    const result = await receiveService.copyAddressToClipboard()
    if (result.success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-text-main" />
        </button>
        <h1 className="text-3xl font-bold text-text-main">{t('receive.title')}</h1>
      </div>

      <div className="bg-card border border-border-base rounded-3xl p-8 flex flex-col items-center gap-8 shadow-xl">
        <p className="text-center text-text-secondary font-medium">
          {t('receive.scan_desc')}
        </p>

        <div className="relative p-4 bg-white rounded-2xl">
          {qrLoading ? (
            <Skeleton className="w-64 h-64 rounded-xl" />
          ) : qrDataUrl ? (
            <img src={qrDataUrl} alt={t('receive.qr_alt')} className="w-64 h-64" />
          ) : (
            <div className="w-64 h-64 bg-text-main/5 flex items-center justify-center rounded-xl">
              <p className="text-text-disabled text-xs text-center px-4">
                {t('receive.qr_error')}
              </p>
            </div>
          )}
        </div>

        <div className="w-full flex flex-col gap-3">
          <p className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('receive.wallet_address_label')}
          </p>
          <div
            onClick={handleCopy}
            className="flex items-center justify-between p-4 rounded-2xl bg-text-main/5 border border-transparent hover:border-primary/30 cursor-pointer transition-all group"
          >
            <p className="text-text-main font-mono text-sm break-all pr-4">
              {address || '0x...'}
            </p>
            <div className="shrink-0">
              {copied ? (
                <Check className="w-5 h-5 text-accent" />
              ) : (
                <Copy className="w-5 h-5 text-text-secondary group-hover:text-primary" />
              )}
            </div>
          </div>
          {copied && (
            <p className="text-accent text-xs font-medium animate-in fade-in slide-in-from-top-1">
              {t('common.copied_clipboard')}
            </p>
          )}
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/10 rounded-2xl p-4">
        <p className="text-xs text-primary/80 leading-relaxed">
          <span className="font-bold">{t('common.notice_label')}</span>{' '}
          {t('receive.notice_desc')}
        </p>
      </div>
    </div>
  )
}

export default Receive
